import { ArrowUpRight, GitCommitHorizontal, GitPullRequest } from "lucide-react";
import { Link } from "react-router";
import type { ReleaseChangeCommit, ReleaseChangePullRequest, ReleaseChanges } from "../../types";
import { TimeAgo } from "../time-ago";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";

/**
 * What the release's tag holds beyond its issue list: pull requests and
 * commits Skynet did not build, and Skynet work planned for another release.
 * For an open release this is what shipping now would carry.
 */
export function ReleaseChangesCard({
  releaseId,
  changes,
  open,
}: {
  releaseId: string;
  changes: ReleaseChanges;
  open: boolean;
}) {
  const untracked = changes.pullRequests.filter((pr) => !pr.issue);
  const elsewhere = changes.pullRequests.filter((pr) => pr.issue && pr.issue.releaseId !== releaseId);
  const untrackedCount = untracked.length + changes.commits.length;
  return (
    <Card className="gap-0 py-0">
      <CardHeader className="border-b px-4 py-3">
        <CardTitle className="flex items-baseline justify-between gap-3 text-sm">
          Not tracked in Skynet
          <span className="font-normal text-muted-foreground tabular-nums">{untrackedCount}</span>
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          {open ? "Shipping now would also carry these" : "Also in this release"}: changes between{" "}
          <a
            href={changes.compareUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-0.5 font-mono hover:text-foreground hover:underline"
          >
            {changes.base}…{changes.head.slice(0, 7)} <ArrowUpRight className="size-3" />
          </a>{" "}
          that no Skynet issue carries · checked <TimeAgo at={changes.checkedAt} />
          {changes.partial ? " · partial: the range is longer than Skynet reads" : ""}
        </p>
      </CardHeader>
      <CardContent className="px-0">
        {untrackedCount === 0 ? (
          <p className="px-4 py-6 text-sm text-muted-foreground">
            Every change in this range came through Skynet.
          </p>
        ) : (
          <ul className="divide-y">
            {untracked.map((pr) => (
              <PullRequestRow key={pr.number} pr={pr} />
            ))}
            {changes.commits.map((commit) => (
              <CommitRow key={commit.sha} commit={commit} />
            ))}
          </ul>
        )}
        {elsewhere.length > 0 && (
          <div className="border-t px-4 py-3">
            <p className="text-xs font-medium">Skynet work planned for another release</p>
            <ul className="mt-1.5 space-y-1">
              {elsewhere.map((pr) => (
                <li key={pr.number} className="flex min-w-0 items-center gap-2 text-sm">
                  <Link
                    to={`/issues/${pr.issue!.identifier}`}
                    className="shrink-0 font-mono text-xs text-muted-foreground hover:text-foreground hover:underline"
                  >
                    {pr.issue!.identifier}
                  </Link>
                  <span className="truncate">{pr.title}</span>
                  <PullRequestLink pr={pr} />
                </li>
              ))}
            </ul>
            <p className="mt-1.5 text-xs text-muted-foreground">
              {open
                ? "Already merged on this branch. Issues waiting in the next standard release, or in no release, move into this one when it publishes."
                : "Their pull requests are in this tag."}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function PullRequestLink({ pr }: { pr: ReleaseChangePullRequest }) {
  return (
    <a
      href={pr.url}
      target="_blank"
      rel="noreferrer"
      className="ml-auto inline-flex shrink-0 items-center gap-1 font-mono text-xs text-muted-foreground hover:text-foreground hover:underline"
    >
      <GitPullRequest className="size-3.5" />#{pr.number}
    </a>
  );
}

function PullRequestRow({ pr }: { pr: ReleaseChangePullRequest }) {
  return (
    <li className="flex items-center gap-3 px-4 py-2.5 hover:bg-muted/40">
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">{pr.title}</p>
        {pr.author && <p className="truncate text-xs text-muted-foreground">{pr.author}</p>}
      </div>
      <PullRequestLink pr={pr} />
    </li>
  );
}

function CommitRow({ commit }: { commit: ReleaseChangeCommit }) {
  return (
    <li className="flex items-center gap-3 px-4 py-2.5 hover:bg-muted/40">
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">{commit.title}</p>
        <p className="truncate text-xs text-muted-foreground">
          pushed without a pull request{commit.author ? ` by ${commit.author}` : ""}
        </p>
      </div>
      <a
        href={commit.url}
        target="_blank"
        rel="noreferrer"
        className="ml-auto inline-flex shrink-0 items-center gap-1 font-mono text-xs text-muted-foreground hover:text-foreground hover:underline"
      >
        <GitCommitHorizontal className="size-3.5" />
        {commit.sha.slice(0, 7)}
      </a>
    </li>
  );
}
